import { ICRUDService } from '../../../reusableFeatures';
import { ERPApiReadConfigurationApi } from '../../../services/backendServices/Apis';
import {
    ERPApiReadConfigurationFormViewModel,
    ERPApiReadConfigurationViewModel,
    ERPApiReadConfigurationFormSchema,
} from '../../../services/backendServices/ViewModels/ERPApiReadConfigurationViewModel';
import { ERPViewModel } from '../../../services/backendServices/ViewModels/ERPViewModel';
import {
    mergeERPConnectionInfo,
    extractNonParameterFields,
    mergeParametersWithPreservedFields,
    convertERPConnectionInfoObjectToParams,
} from './ERPConnectionInfo.utils';
import { useMemo } from 'react';

export class ERPApiReadConfigurationCRUDService implements ICRUDService<number, ERPApiReadConfigurationFormViewModel> {
    private api = new ERPApiReadConfigurationApi();

    private baseERP?: ERPViewModel;

    private erpData: ERPViewModel[];

    private companyId: number;

    // Store only the non-parameter fields to preserve backend-managed XML
    private preservedNonParameterFields: Record<string, any> = {};

    constructor(baseERP?: ERPViewModel, erpData: ERPViewModel[] = [], companyId?: number) {
        this.baseERP = baseERP;
        this.erpData = erpData;
        this.companyId = companyId ?? 0;
    }

    get defaultState(): ERPApiReadConfigurationFormViewModel {
        return {
            Id: null,
            CompanyId: this.companyId,
            InvoiceProfileId: 0,
            ERPName: this.baseERP?.ERPName ?? '',
            ERPVersion: this.baseERP?.ERPVersion ?? '',
            ERPConnectionInfo: this.baseERP?.ERPConnectionMetadata ?? { root: { Parameters: { Parameter: [] } } },
            Active: this.baseERP ? true : false,
            LastReadTime: null,
        };
    }

    get key() {
        return 'ERPApiReadConfiguration';
    }

    get invalidateKeys(): string[] {
        return ['ERPApiReadConfiguration'];
    }

    async fetchEntity(id: number): Promise<ERPApiReadConfigurationFormViewModel> {
        const response = await this.api.getERPApiReadConfiguration(id);
        const entity = response.data;

        // Extract and store non-parameter fields (LastReads, BackendField, etc.)
        this.preservedNonParameterFields = extractNonParameterFields(entity.ERPConnectionInfo);

        let mergedERPConnectionInfo = entity.ERPConnectionInfo;

        // Use baseERP if provided, otherwise search through erpData
        let matchingERP = this.baseERP;
        if (!matchingERP && this.erpData.length > 0 && entity.ERPName && entity.ERPVersion) {
            matchingERP = this.erpData.find((e) => e.ERPName === entity.ERPName && e.ERPVersion === entity.ERPVersion);
        }

        if (matchingERP && matchingERP.ERPConnectionMetadata) {
            mergedERPConnectionInfo = mergeERPConnectionInfo(
                matchingERP.ERPConnectionMetadata,
                entity.ERPConnectionInfo
            );
        }

        const formData = this.mapFromViewModel({ ...entity, ERPConnectionInfo: mergedERPConnectionInfo });

        return formData;
    }

    async updateEntity(id: number, state: ERPApiReadConfigurationFormViewModel): Promise<void> {
        const parsedForm = ERPApiReadConfigurationFormSchema.safeParse(state);

        if (!parsedForm.success) {
            let friendlyErrorMessage = '\nError(s) occurred while updating ERP API Configuration:\n';

            const errors = parsedForm.error.errors;
            const formattedErrors = errors.map((error) => {
                const message = error.message;
                return `${message}`;
            });

            // format errors for user
            friendlyErrorMessage += formattedErrors.map((error) => `- ${error}`).join('\n');

            throw new Error(friendlyErrorMessage);
        }

        const viewModel = this.mapToViewModel(state, id);
        await this.api.putERPApiReadConfiguration(id, viewModel);
    }

    async createEntity(state: ERPApiReadConfigurationFormViewModel): Promise<number> {
        const parsedForm = ERPApiReadConfigurationFormSchema.safeParse(state);

        if (!parsedForm.success) {
            let friendlyErrorMessage = '\nError(s) occurred while creating ERP API Configuration:\n';

            const errors = parsedForm.error.errors;
            const formattedErrors = errors.map((error) => {
                const message = error.message;
                return `${message}`;
            });

            // format errors for user
            friendlyErrorMessage += formattedErrors.map((error) => `- ${error}`).join('\n');

            throw new Error(friendlyErrorMessage);
        }

        const viewModel = this.mapToViewModel(state, null);
        const response = await this.api.postERPApiReadConfiguration(viewModel);

        return response.data.Id || 0;
    }

    async deleteEntity(): Promise<void> {
        throw new Error('not implemented');
    }

    private mapFromViewModel(viewModel: ERPApiReadConfigurationViewModel): ERPApiReadConfigurationFormViewModel {
        return {
            Id: viewModel.Id,
            CompanyId: viewModel.CompanyID,
            InvoiceProfileId: viewModel.InvoiceProfileId,
            ERPName: viewModel.ERPName,
            ERPVersion: viewModel.ERPVersion,
            ERPConnectionInfo: viewModel.ERPConnectionInfo,
            Active: viewModel.Active,
            LastReadTime: viewModel.LastReadTime,
        };
    }

    private mapToViewModel(
        formData: ERPApiReadConfigurationFormViewModel,
        id: number | null = null
    ): ERPApiReadConfigurationViewModel {
        // Extract parameters from form data
        const formParams = convertERPConnectionInfoObjectToParams(formData.ERPConnectionInfo);

        // Merge parameters with preserved non-parameter fields
        const preservedConnectionInfo = mergeParametersWithPreservedFields(
            formParams,
            this.preservedNonParameterFields
        );

        return {
            Id: id,
            CompanyID: formData.CompanyId,
            InvoiceProfileId: formData.InvoiceProfileId,
            CreatedTime: null,
            LastUpdateDate: new Date(),
            ERPName: formData.ERPName,
            ERPVersion: formData.ERPVersion,
            ERPConnectionInfo: preservedConnectionInfo,
            LastReadTime: formData.LastReadTime,
            Active: formData.Active,
            RowVersion: '',
        };
    }
}

export const useERPApiReadConfigurationCRUDService = (
    baseERP?: ERPViewModel,
    erpData: ERPViewModel[] = [],
    companyId?: number
) => {
    return useMemo(
        () => new ERPApiReadConfigurationCRUDService(baseERP, erpData, companyId),
        [baseERP, erpData, companyId]
    );
};
